import { zodResolver } from "@hookform/resolvers/zod";
import { addHours, addMinutes, format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Controller, useForm } from "react-hook-form";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";
import { useAppSelector } from "../../../redux/hooks";
import { useToast } from "../../toast-provider";
import useContacts from "../../ContactsList/use-contacts";
import { MyCalendar } from "../../MyCalendar";
import { v } from "../../../util";
import useCalendar, {
  CalendarAppointmentConfirmationChannel,
  CalendarAppointmentItem
} from "../Calendar/use-calendar";
import {
  CalendarFieldWrapper,
  CalendarRow,
  CheckboxRow,
  ContactOptionContent,
  ContactOptionName,
  ContactOptionPhone,
  Content,
  Container,
  Field,
  FieldLabel,
  Footer,
  FloatingField,
  Form,
  FormDropdown,
  FormInput,
  FormTextarea,
  Header,
  IconWrapper,
  PrimaryFooterButton,
  SecondaryFooterButton,
  TopFieldLabel,
  Title
} from "./styles";

type Props = {
  appointmentId?: number | null;
  currentCalendarId?: number | null;
  currentCalendarBreakAfterMinutes?: number | null;
  existingAppointments?: CalendarAppointmentItem[];
  initialDate?: Date | null;
  canDeleteAppointment?: boolean;
  canSaveAppointment?: boolean;
  onClose?: () => void;
  onSuccess?: () => void | Promise<void>;
};

const channelOptions = [
  { label: "WhatsApp", value: "WHATSAPP" }
  // { label: "E-mail", value: "EMAIL" }
] as const;

type ConfirmationChannel = (typeof channelOptions)[number]["value"];

type CalendarAppointmentFormValues = {
  name: string;
  contactId: number | null;
  startDate: Date;
  endDate: Date;
  description: string;
  sendConfirmationMessage: boolean;
  channelConfirmationMessage: ConfirmationChannel | null;
  confirmationMessageDate: Date;
};

type ContactOption = {
  label: string;
  value: number;
  name: string;
  phone: string;
  email: string;
};

type AppointmentConflictType = "overlap" | "break";

const formatPhone = (phoneNumber: string): string => {
  const numericOnly = phoneNumber.replace(/\D/gu, "");

  if (!numericOnly) {
    return phoneNumber;
  }

  if (numericOnly.length > 10) {
    const countrySplitter = require("country-code-splitter");

    try {
      const splittedPhone = countrySplitter.getNumber(`${numericOnly}`);

      if (!splittedPhone?.number) {
        return phoneNumber;
      }

      const ddd = splittedPhone.number.substring(0, 2);
      const firstHalf = splittedPhone.number.substring(
        2,
        splittedPhone.number.length - 4
      );
      const secondHalf = splittedPhone.number.substring(
        splittedPhone.number.length - 4
      );

      return `${splittedPhone.code} (${ddd}) ${firstHalf}-${secondHalf}`;
    } catch {
      return phoneNumber;
    }
  }

  const ddd = numericOnly.substring(0, 2);
  const firstHalf = numericOnly.substring(2, numericOnly.length - 4);
  const secondHalf = numericOnly.substring(numericOnly.length - 4);

  return `(${ddd}) ${firstHalf}-${secondHalf}`;
};

const getMinimumFutureNotificationDate = () => addMinutes(new Date(), 1);

const getDefaultConfirmationMessageDate = (startDate: Date) => {
  const suggestedDate = addHours(startDate, -24);
  const minimumDate = getMinimumFutureNotificationDate();

  return suggestedDate > minimumDate ? suggestedDate : minimumDate;
};

export const CalendarAppointment: React.FC<Props> = ({
  appointmentId,
  currentCalendarId,
  currentCalendarBreakAfterMinutes,
  existingAppointments = [],
  initialDate,
  canDeleteAppointment = false,
  canSaveAppointment = true,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth() as AuthContextType;
  const isReadOnly = !canSaveAppointment;
  const { ListContacts } = useContacts();
  const {
    createCalendarAppointment,
    deleteCalendarAppointment,
    getCalendarAppointment,
    updateCalendarAppointment
  } = useCalendar();
  const toast = useToast();
  const selectedWorkspacesIds = useAppSelector(
    (state) => state.workspaces.selectedWorkspaces
  );
  const selectedWorkspacesIdsRef = useRef<number[]>(
    selectedWorkspacesIds ?? []
  );
  const userWorkspaceIdsRef = useRef<number[]>(
    user?.workspaces?.workspaces?.map((workspace) => workspace.id) || []
  );
  const loadedAppointmentIdRef = useRef<number | null>(null);
  const [contactOptions, setContactOptions] = useState<ContactOption[]>([]);
  const [notificationTouched, setNotificationTouched] = useState(false);
  const [pendingConflictData, setPendingConflictData] =
    useState<CalendarAppointmentFormValues | null>(null);
  const [conflictDialogType, setConflictDialogType] =
    useState<AppointmentConflictType | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const nowRef = useRef(new Date());
  const now = nowRef.current;
  const schema = v
    .object({
      name: v.string().superRefine(v.nonempty()),
      contactId: v.number().nullable(),
      startDate: v.date(),
      endDate: v.date(),
      description: v.string(),
      sendConfirmationMessage: v.boolean(),
      channelConfirmationMessage: v
        .enumeration(["WHATSAPP"] satisfies [CalendarAppointmentConfirmationChannel])
        .nullable(),
      confirmationMessageDate: v.date()
    })
    .superRefine((values, ctx) => {
      if (values.endDate < values.startDate) {
        ctx.addIssue({
          code: "custom",
          path: ["endDate"],
          message: "Data final deve ser maior ou igual à data inicial"
        });
      }

      if (values.confirmationMessageDate > values.startDate) {
        ctx.addIssue({
          code: "custom",
          path: ["confirmationMessageDate"],
          message: "Data da notificação não pode ser depois da data inicial"
        });
      }

      if (values.sendConfirmationMessage && values.contactId == null) {
        ctx.addIssue({
          code: "custom",
          path: ["contactId"],
          message: "Selecione um contato para enviar a confirmação."
        });
      }

      if (
        values.sendConfirmationMessage &&
        values.channelConfirmationMessage == null
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["channelConfirmationMessage"],
          message: "Selecione um canal para enviar a confirmação."
        });
      }
    });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset
  } = useForm<CalendarAppointmentFormValues>({
    defaultValues: {
      name: "",
      contactId: null,
      startDate: now,
      endDate: now,
      description: "",
      sendConfirmationMessage: false,
      channelConfirmationMessage: null,
      confirmationMessageDate: getDefaultConfirmationMessageDate(now)
    },
    resolver: zodResolver(schema),
    shouldFocusError: true
  });

  const startDateValue = watch("startDate");
  const selectedContactId = watch("contactId");
  const sendConfirmationMessageValue = watch("sendConfirmationMessage");
  const selectedChannelValue = watch("channelConfirmationMessage");
  const selectedContact =
    contactOptions.find((contact) => contact.value === selectedContactId) ??
    null;
  const availableChannelOptions = selectedContact ? [...channelOptions] : [];
  const canConfigureConfirmation = selectedContact != null;

  const getContactOption = (
    option: ContactOption | number | null | undefined
  ) => {
    if (option == null) {
      return null;
    }

    if (typeof option === "number") {
      return contactOptions.find((contact) => contact.value === option) ?? null;
    }

    return option;
  };

  const renderContactOption = (
    option: ContactOption | number | null | undefined
  ) => {
    const resolvedOption = getContactOption(option);

    if (!resolvedOption) {
      return null;
    }

    const primaryLabel = resolvedOption.name || resolvedOption.label;
    const shouldShowPhone =
      !!resolvedOption.phone && resolvedOption.phone !== primaryLabel;

    return (
      <ContactOptionContent>
        <ContactOptionName>{primaryLabel}</ContactOptionName>
        {shouldShowPhone ? (
          <ContactOptionPhone>
            {formatPhone(resolvedOption.phone)}
          </ContactOptionPhone>
        ) : null}
      </ContactOptionContent>
    );
  };

  const renderSelectedContactValue = (
    option: ContactOption | number | null | undefined,
    props?: { placeholder?: string }
  ) => {
    const resolvedOption = getContactOption(option);

    if (!resolvedOption) {
      return (
        <span className="p-placeholder">{props?.placeholder || "Contato"}</span>
      );
    }

    return (
      <ContactOptionName>
        {resolvedOption.name || resolvedOption.label}
      </ContactOptionName>
    );
  };

  useEffect(() => {
    selectedWorkspacesIdsRef.current = selectedWorkspacesIds ?? [];
  }, [selectedWorkspacesIds]);

  useEffect(() => {
    userWorkspaceIdsRef.current =
      user?.workspaces?.workspaces?.map((workspace) => workspace.id) || [];
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const loadContacts = async () => {
      const workspaceIds =
        selectedWorkspacesIdsRef.current &&
        selectedWorkspacesIdsRef.current.length > 0
          ? selectedWorkspacesIdsRef.current
          : userWorkspaceIdsRef.current;

      if (workspaceIds.length === 0) {
        setTimeout(() => {
          if (!cancelled) {
            loadContacts();
          }
        }, 500);
        return;
      }

      try {
        const data = await ListContacts({
          workspaces: workspaceIds,
          offset: 0,
          limit: 1000
        });

        if (cancelled) {
          return;
        }

        const options = (data?.result || []).map((contact: any) => ({
          label: contact.name || contact.phone || `Contato ${contact.id}`,
          value: contact.id,
          name: contact.name || "",
          phone: contact.phone || "",
          email: contact.email || ""
        }));

        setContactOptions(options);
      } catch (error) {
        console.error("Erro ao buscar contatos:", error);
        setContactOptions([]);
      }
    };

    loadContacts();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!startDateValue || notificationTouched) {
      return;
    }

    if (appointmentId) {
      return;
    }

    setValue(
      "confirmationMessageDate",
      getDefaultConfirmationMessageDate(startDateValue)
    );
  }, [appointmentId, notificationTouched, setValue, startDateValue]);

  useEffect(() => {
    const loadAppointment = async () => {
      if (!appointmentId) {
        loadedAppointmentIdRef.current = null;
        setNotificationTouched(false);
        const baseDate = initialDate ?? now;
        reset({
          name: "",
          contactId: null,
          startDate: baseDate,
          endDate: baseDate,
          description: "",
          sendConfirmationMessage: false,
          channelConfirmationMessage: null,
          confirmationMessageDate: getDefaultConfirmationMessageDate(baseDate)
        });
        return;
      }

      if (loadedAppointmentIdRef.current === appointmentId) {
        return;
      }

      try {
        const response = await getCalendarAppointment(appointmentId);
        loadedAppointmentIdRef.current = appointmentId;

        reset({
          name: response.name ?? "",
          contactId: response.contactId ?? null,
          startDate: new Date(response.startDate),
          endDate: new Date(response.endDate),
          description: response.description ?? "",
          sendConfirmationMessage: response.sendConfirmationMessage,
          channelConfirmationMessage:
            response.channelConfirmationMessage === "WHATSAPP"
              ? response.channelConfirmationMessage
              : null,
          confirmationMessageDate: response.confirmationMessageDate
            ? new Date(response.confirmationMessageDate)
            : getDefaultConfirmationMessageDate(new Date(response.startDate))
        });
        setNotificationTouched(true);
      } catch (error) {
        showErrorToast(error, "Não foi possível carregar o compromisso.");
      }
    };

    loadAppointment();
  }, [appointmentId, initialDate]);

  useEffect(() => {
    if (!selectedContact) {
      if (sendConfirmationMessageValue) {
        setValue("sendConfirmationMessage", false);
      }

      if (selectedChannelValue !== null) {
        setValue("channelConfirmationMessage", null);
      }

      return;
    }

    if (availableChannelOptions.length === 0) {
      if (sendConfirmationMessageValue) {
        setValue("sendConfirmationMessage", false);
      }

      if (selectedChannelValue !== null) {
        setValue("channelConfirmationMessage", null);
      }

      return;
    }

    if (!sendConfirmationMessageValue) {
      if (selectedChannelValue !== null) {
        setValue("channelConfirmationMessage", null);
      }

      return;
    }

    const channelStillAvailable = availableChannelOptions.some(
      (option) => option.value === selectedChannelValue
    );

    if (!channelStillAvailable) {
      setValue("channelConfirmationMessage", availableChannelOptions[0].value);
    }
  }, [
    availableChannelOptions,
    selectedChannelValue,
    selectedContact,
    sendConfirmationMessageValue,
    setValue
  ]);

  const formatPayloadDate = (value: Date) =>
    format(value, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");

  const getAppointmentConflict = (
    startDate: Date,
    endDate: Date
  ): "overlap" | "break" | null => {
    const relevantAppointments = existingAppointments.filter(
      (appointment) => appointment.id !== appointmentId
    );

    const hasOverlap = relevantAppointments.some((appointment) => {
      const appointmentStart = new Date(appointment.startDate);
      const appointmentEnd = new Date(appointment.endDate);

      return startDate < appointmentEnd && endDate > appointmentStart;
    });

    if (hasOverlap) {
      return "overlap";
    }

    const breakAfterMinutes = currentCalendarBreakAfterMinutes ?? 0;

    if (breakAfterMinutes <= 0) {
      return null;
    }

    const hasBreakConflict = relevantAppointments.some((appointment) => {
      const appointmentStart = new Date(appointment.startDate);
      const appointmentEnd = new Date(appointment.endDate);

      if (endDate <= appointmentStart) {
        return addMinutes(endDate, breakAfterMinutes) > appointmentStart;
      }

      if (appointmentEnd <= startDate) {
        return addMinutes(appointmentEnd, breakAfterMinutes) > startDate;
      }

      return false;
    });

    return hasBreakConflict ? "break" : null;
  };

  const submitAppointment = async (data: CalendarAppointmentFormValues) => {
    const trimmedDescription = data.description.trim();
    const shouldSendConfirmation =
      data.sendConfirmationMessage &&
      data.contactId != null &&
      data.channelConfirmationMessage != null;
    const payload = {
      name: data.name.trim(),
      calendarId: currentCalendarId!,
      contactId: data.contactId ?? null,
      startDate: formatPayloadDate(data.startDate),
      endDate: formatPayloadDate(data.endDate),
      description: trimmedDescription.length > 0 ? trimmedDescription : null,
      sendConfirmationMessage: shouldSendConfirmation,
      channelConfirmationMessage: shouldSendConfirmation
        ? data.channelConfirmationMessage
        : null,
      confirmationMessageDate: shouldSendConfirmation
        ? formatPayloadDate(data.confirmationMessageDate)
        : null
    };

    if (appointmentId) {
      await updateCalendarAppointment({
        id: appointmentId,
        ...payload
      });
    } else {
      await createCalendarAppointment(payload);
    }

    toast?.current?.show({
      life: 3000,
      severity: "success",
      summary: "Sucesso",
      detail: appointmentId
        ? "Compromisso atualizado com sucesso."
        : "Compromisso criado com sucesso."
    });

    await onSuccess?.();
  };

  const showConflictDialog = (
    type: AppointmentConflictType,
    data: CalendarAppointmentFormValues
  ) => {
    setConflictDialogType(type);
    setPendingConflictData(data);
  };

  const hideConflictDialog = () => {
    setConflictDialogType(null);
    setPendingConflictData(null);
  };

  const acceptConflictDialog = async () => {
    if (!pendingConflictData) {
      hideConflictDialog();
      return;
    }

    const data = pendingConflictData;
    hideConflictDialog();
    await submitAppointment(data);
  };

  const showErrorToast = (error: any, fallbackMessage: string) => {
    const backendMessage =
      error?.response?.data?.message || error?.message || fallbackMessage;

    toast?.current?.show({
      life: 5000,
      severity: "error",
      summary: "Erro",
      detail: backendMessage
    });
  };

  const handleDeleteAppointment = async () => {
    if (!appointmentId) {
      setShowDeleteDialog(false);
      return;
    }

    try {
      await deleteCalendarAppointment(appointmentId);

      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso",
        detail: "Compromisso excluído com sucesso."
      });

      setShowDeleteDialog(false);
      await onSuccess?.();
    } catch (error) {
      setShowDeleteDialog(false);
      showErrorToast(error, "Não foi possível excluir o compromisso.");
    }
  };

  const handleSaveAppointment = handleSubmit(async (data) => {
    if (!currentCalendarId) {
      toast?.current?.show({
        life: 5000,
        severity: "warn",
        summary: "Atenção",
        detail: "Nenhum calendário foi selecionado."
      });
      return;
    }

    try {
      const conflictType = getAppointmentConflict(data.startDate, data.endDate);

      if (conflictType === "overlap") {
        showConflictDialog("overlap", data);
        return;
      }

      if (conflictType === "break") {
        showConflictDialog("break", data);
        return;
      }

      await submitAppointment(data);
    } catch (error) {
      showErrorToast(error, "Não foi possível salvar o compromisso.");
    }
  });

  return (
    <Container>
      <Dialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        draggable={false}
        header="Excluir compromisso"
        style={{ width: "575px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            Ao prosseguir, o compromisso será excluído e essa ação não poderá
            ser desfeita. Tem certeza que deseja continuar?
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "15px"
            }}
          >
            <Button
              label="Sim"
              className="p-button-danger"
              onClick={() => {
                void handleDeleteAppointment();
              }}
            />
            <Button
              label="Não"
              className="p-button-secondary"
              onClick={() => setShowDeleteDialog(false)}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={conflictDialogType !== null}
        onHide={hideConflictDialog}
        draggable={false}
        header={
          conflictDialogType === "overlap"
            ? "Conflito de horários"
            : "Intervalo entre agendamentos"
        }
        style={{ width: "575px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            {conflictDialogType === "overlap"
              ? "Você está tentando criar um agendamento em um horário que já tem outro agendamento. Tem certeza que deseja continuar?"
              : "Esse agendamento não respeita o tempo mínimo de intervalo configurado entre reuniões. Tem certeza que deseja continuar?"}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "15px"
            }}
          >
            <Button
              label="Sim"
              className="p-button-danger"
              onClick={() => {
                void acceptConflictDialog();
              }}
            />
            <Button
              label="Não"
              className="p-button-secondary"
              onClick={hideConflictDialog}
            />
          </div>
        </div>
      </Dialog>

      <Content>
        <Header>
          <IconWrapper>
            <i className="pi pi-calendar" />
          </IconWrapper>
          <Title>{appointmentId ? "Editar Tarefa" : "Detalhe da Tarefa"}</Title>
        </Header>

        <Form>
          <Field>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <FormInput
                  {...field}
                  value={field.value ?? ""}
                  placeholder="Nome da tarefa"
                  disabled={isReadOnly}
                />
              )}
            />
            {errors.name?.message ? (
              <div style={{ color: "#AA3939" }}>{errors.name.message}</div>
            ) : null}
          </Field>

          <Field>
            <Controller
              name="contactId"
              control={control}
              render={({ field }) => (
                <FormDropdown
                  value={field.value}
                  onChange={(e) => field.onChange(e.value ?? null)}
                  options={contactOptions}
                  optionLabel="label"
                  optionValue="value"
                  itemTemplate={renderContactOption}
                  valueTemplate={renderSelectedContactValue}
                  filterBy="label,name,phone"
                  appendTo="self"
                  placeholder="Contato"
                  emptyFilterMessage="Sem resultados"
                  emptyMessage="Sem resultados"
                  filter
                  showClear
                  disabled={isReadOnly}
                />
              )}
            />
            {errors.contactId?.message ? (
              <div style={{ color: "#AA3939" }}>{errors.contactId.message}</div>
            ) : null}
          </Field>

          <CalendarRow>
            <Field>
              <TopFieldLabel>Data inicial</TopFieldLabel>
              <CalendarFieldWrapper>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <MyCalendar
                      value={field.value}
                      onChange={(e) => field.onChange(e.value as Date | null)}
                      showTime
                      hourFormat="24"
                      dateFormat="dd/mm/yy"
                      showIcon
                      style={{
                        border: "1px solid #ced4da",
                        borderRadius: "4px"
                      }}
                      placeholder="Escolha a data inicial"
                      panelClassName="custom-calendar-panel"
                      disabled={isReadOnly}
                    />
                  )}
                />
              </CalendarFieldWrapper>
              {errors.startDate?.message ? (
                <div style={{ color: "#AA3939" }}>
                  {errors.startDate.message}
                </div>
              ) : null}
            </Field>

            <Field>
              <TopFieldLabel>Data final</TopFieldLabel>
              <CalendarFieldWrapper>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <MyCalendar
                      value={field.value}
                      onChange={(e) => field.onChange(e.value as Date | null)}
                      showTime
                      hourFormat="24"
                      dateFormat="dd/mm/yy"
                      showIcon
                      style={{
                        border: "1px solid #ced4da",
                        borderRadius: "4px"
                      }}
                      placeholder="Escolha a data final"
                      panelClassName="custom-calendar-panel"
                      disabled={isReadOnly}
                    />
                  )}
                />
              </CalendarFieldWrapper>
              {errors.endDate?.message ? (
                <div style={{ color: "#AA3939" }}>{errors.endDate.message}</div>
              ) : null}
            </Field>
          </CalendarRow>

          <Field>
            <FloatingField>
              <FieldLabel>Descrição</FieldLabel>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <FormTextarea
                    {...field}
                    value={field.value ?? ""}
                    placeholder="A descrição da tarefa"
                    autoResize
                    disabled={isReadOnly}
                  />
                )}
              />
            </FloatingField>
          </Field>

          <CheckboxRow>
            <Controller
              name="sendConfirmationMessage"
              control={control}
              render={({ field }) => (
                <Checkbox
                  inputId="sendConfirmationMessage"
                  checked={field.value}
                  onChange={(e) => {
                    const nextChecked = !!e.checked;

                    field.onChange(nextChecked);

                    if (nextChecked) {
                      setValue(
                        "channelConfirmationMessage",
                        selectedChannelValue ??
                          availableChannelOptions[0]?.value ??
                          null
                      );
                      return;
                    }

                    setValue("channelConfirmationMessage", null);
                  }}
                  disabled={isReadOnly || !canConfigureConfirmation}
                />
              )}
            />
            <label htmlFor="sendConfirmationMessage">
              Enviar mensagem de confirmação
            </label>
          </CheckboxRow>

          <Field>
            <Controller
              name="channelConfirmationMessage"
              control={control}
              render={({ field }) => (
                <FormDropdown
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  options={availableChannelOptions}
                  optionLabel="label"
                  optionValue="value"
                  appendTo="self"
                  placeholder="Canal da confirmação"
                  disabled={
                    isReadOnly ||
                    !sendConfirmationMessageValue ||
                    !canConfigureConfirmation
                  }
                />
              )}
            />
            {errors.channelConfirmationMessage?.message ? (
              <div style={{ color: "#AA3939" }}>
                {errors.channelConfirmationMessage.message}
              </div>
            ) : null}
          </Field>

          <Field>
            <TopFieldLabel>Data da notificação</TopFieldLabel>
            <CalendarFieldWrapper>
              <Controller
                name="confirmationMessageDate"
                control={control}
                render={({ field }) => (
                  <MyCalendar
                    value={field.value}
                    onChange={(e) => {
                      setNotificationTouched(true);
                      field.onChange(e.value as Date | null);
                    }}
                    showTime
                    hourFormat="24"
                    dateFormat="dd/mm/yy"
                    showIcon
                    style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                    placeholder="Escolha a data da notificação"
                    panelClassName="custom-calendar-panel"
                    disabled={isReadOnly || !sendConfirmationMessageValue}
                  />
                )}
              />
            </CalendarFieldWrapper>
            {errors.confirmationMessageDate?.message ? (
              <div style={{ color: "#AA3939" }}>
                {errors.confirmationMessageDate.message}
              </div>
            ) : null}
          </Field>

          {appointmentId && canDeleteAppointment ? (
            <Button
              title="Excluir compromisso"
              label="EXCLUIR COMPROMISSO"
              onClick={() => setShowDeleteDialog(true)}
              className="p-button-outlined p-button-danger mt-3 text-xs"
              style={{ width: "100%" }}
              disabled={isSubmitting}
            />
          ) : null}
        </Form>
      </Content>

      <Footer>
        {canSaveAppointment ? (
          <>
            <SecondaryFooterButton
              label="CANCELAR"
              className="no-hover-button"
              onClick={onClose}
              disabled={isSubmitting}
            />
            <PrimaryFooterButton
              label="SALVAR"
              color={user.plataform.mainColor || "#2624CD"}
              onClick={handleSaveAppointment}
              disabled={isSubmitting}
            />
          </>
        ) : (
          <SecondaryFooterButton
            label="VOLTAR"
            className="no-hover-button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{ width: "100%" }}
          />
        )}
      </Footer>
    </Container>
  );
};

export default CalendarAppointment;
